<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import TasksAPI from 'dashboard/api/tasks';
import KanbanAPI from 'dashboard/api/kanban';
import ContactAPI from 'dashboard/api/contacts';
import { PRIORITY_META, PRIORITY_ORDER } from '../constants/priorityColors';
import TaskComments from './TaskComments.vue';
import TaskDatePicker from './TaskDatePicker.vue';
import SmartTitleInput from './SmartTitleInput.vue';
import {
  buildDueIso,
  dueDateKey,
  recurrenceEndsToIso,
  recurrenceEndsToPicker,
  splitDue,
} from '../helpers/dueDate';

const props = defineProps({
  taskId: { type: Number, required: true },
  openWithChecklistWarning: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'updated', 'deleted', 'openCard']);

const { t } = useI18n();
const store = useStore();
const { updateTask, deleteTask: destroyTask } = useTaskActions();
const { accountId } = useAccount();
const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

const task = computed(() => store.getters['tasks/currentTask']);
const isLoading = ref(false);
const titleValue = ref('');
const descriptionValue = ref('');
const showChecklistWarning = ref(false);

// histórico via API
const activities = ref([]);
const revertingId = ref(null);
const confirmingId = ref(null);

// ── checklist ────────────────────────────────────────────────────
const newItemText = ref('');
const newItemInputRef = ref(null);
const showChecklistAssignee = ref(null);
const dragSrcIndex = ref(null);
const dragOverIndex = ref(null);

// Cada gravação manda a lista inteira. Enquanto houver gravação pendente a tela
// usa o rascunho local e as requisições saem em fila: montar a lista a partir
// do store fazia o segundo clique rápido desfazer o primeiro.
const checklistDraft = ref(null);
let checklistQueue = Promise.resolve();
let pendingChecklistSaves = 0;

const checklistItems = computed(
  () => checklistDraft.value ?? task.value?.checklist_items ?? []
);

const checklistProgress = computed(() => {
  const items = checklistItems.value;
  if (!items.length) return null;
  const done = items.filter(i => i.completed).length;
  return { done, total: items.length };
});

function saveChecklist(items) {
  const taskId = task.value.id;
  checklistDraft.value = items;
  pendingChecklistSaves += 1;
  checklistQueue = checklistQueue
    .then(async () => {
      const result = await updateTask({ taskId, checklist_items: items });
      if (!result.success) await store.dispatch('tasks/fetchTask', taskId);
    })
    .finally(() => {
      pendingChecklistSaves -= 1;
      if (pendingChecklistSaves === 0) {
        checklistDraft.value = null;
        emit('updated');
      }
    });
  return checklistQueue;
}

async function toggleChecklistItem(itemId) {
  const updated = checklistItems.value.map(i =>
    i.id === itemId ? { ...i, completed: !i.completed } : i
  );
  await saveChecklist(updated);
}

async function addChecklistItem() {
  const text = newItemText.value.trim();
  if (!text) return;
  const newItem = {
    id: crypto.randomUUID(),
    text,
    completed: false,
    assignee_id: null,
  };
  await saveChecklist([...checklistItems.value, newItem]);
  newItemText.value = '';
  nextTick(() => newItemInputRef.value?.focus());
}

async function removeChecklistItem(itemId) {
  const updated = checklistItems.value.filter(i => i.id !== itemId);
  await saveChecklist(updated);
}

function onDragStart(index) {
  dragSrcIndex.value = index;
}

function onDragOver(e, index) {
  e.preventDefault();
  dragOverIndex.value = index;
}

function onDragLeave() {
  dragOverIndex.value = null;
}

async function onDrop(index) {
  if (dragSrcIndex.value === null || dragSrcIndex.value === index) {
    dragSrcIndex.value = null;
    dragOverIndex.value = null;
    return;
  }
  const items = [...checklistItems.value];
  const [moved] = items.splice(dragSrcIndex.value, 1);
  items.splice(index, 0, moved);
  dragSrcIndex.value = null;
  dragOverIndex.value = null;
  await saveChecklist(items);
}

function onDragEnd() {
  dragSrcIndex.value = null;
  dragOverIndex.value = null;
}

async function setChecklistItemAssignee(itemId, agentId) {
  const updated = checklistItems.value.map(i =>
    i.id === itemId ? { ...i, assignee_id: agentId } : i
  );
  showChecklistAssignee.value = null;
  await saveChecklist(updated);
}

// dropdowns
const showPriorityMenu = ref(false);
const prioritySearch = ref('');
const priorityHighlight = ref(0);
const showAssigneeMenu = ref(false);
const assigneeSearch = ref('');
const assigneeHighlight = ref(0);
const showDatePicker = ref(false);
const showTypeMenu = ref(false);
const typeSearch = ref('');
const typeHighlight = ref(0);
const showLabelMenu = ref(false);
const labelSearch = ref('');
const labelHighlight = ref(0);
const showCardMenu = ref(false);
const cardMenuBtnRef = ref(null);
const cardMenuPos = ref({ top: 0, right: 0 });

const TASK_TYPES = [
  { key: 'general', label: 'Geral', icon: 'i-lucide-circle-dot' },
  { key: 'call', label: 'Ligação', icon: 'i-lucide-phone' },
  { key: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { key: 'meeting', label: 'Reunião', icon: 'i-lucide-calendar' },
  { key: 'follow_up', label: 'Follow-up', icon: 'i-lucide-refresh-cw' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { key: 'contract', label: 'Contrato', icon: 'i-lucide-file-signature' },
  { key: 'payment', label: 'Pagamento', icon: 'i-lucide-banknote' },
];

const currentType = computed(
  () => TASK_TYPES.find(tp => tp.key === task.value?.task_type) ?? TASK_TYPES[0]
);

async function saveField(field, value) {
  if (!task.value) return;
  await updateTask({
    taskId: task.value.id,
    [field]: value,
  });
  // eslint-disable-next-line no-use-before-define
  loadActivities();
  emit('updated');
}

async function setTaskType(key) {
  showTypeMenu.value = false;
  await saveField('task_type', key);
}

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  if (!q) return accountLabels.value ?? [];
  return (accountLabels.value ?? [])
    .filter(l => l.title.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.title.toLowerCase().indexOf(q) - b.title.toLowerCase().indexOf(q)
    );
});

const labelNavigable = computed(() =>
  filteredLabels.value.map(l => ({ id: l.title }))
);

watch(labelNavigable, () => {
  labelHighlight.value = 0;
});
watch(labelSearch, () => {
  labelHighlight.value = 0;
});

function isLabelActive(title) {
  return (task.value?.label_list ?? []).includes(title);
}

function tagColor(label) {
  const found = (accountLabels.value ?? []).find(
    l => l.title?.toLowerCase() === label.toLowerCase()
  );
  if (found?.color) return { bg: `${found.color}22`, text: found.color };
  return { bg: 'rgba(99,102,241,0.12)', text: '#4338ca' };
}

async function toggleLabel(title) {
  const existing = task.value?.label_list ?? [];
  const updated = existing.includes(title)
    ? existing.filter(l => l !== title)
    : [...existing, title];
  await saveField('label_list', updated);
}

async function removeLabel(tag) {
  const updated = (task.value?.label_list ?? []).filter(l => l !== tag);
  await saveField('label_list', updated);
}

function onLabelKeydown(e) {
  if (!showLabelMenu.value) return;
  if (e.key === 'Escape') {
    showLabelMenu.value = false;
    labelSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    labelHighlight.value = Math.min(
      labelHighlight.value + 1,
      labelNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    labelHighlight.value = Math.max(labelHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = labelNavigable.value[labelHighlight.value];
    if (item) toggleLabel(item.id);
  } else if (e.key === 'Backspace') {
    labelSearch.value = labelSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    labelSearch.value += e.key;
  }
}

watch(showLabelMenu, val => {
  if (val) {
    labelHighlight.value = 0;
    window.addEventListener('keydown', onLabelKeydown);
  } else {
    labelSearch.value = '';
    window.removeEventListener('keydown', onLabelKeydown);
  }
});

// edição de contato
const editingContact = ref(false);
const contactSearch = ref('');
const contactResults = ref([]);
const showContactDropdown = ref(false);
const contactHighlightedIndex = ref(0);
const contactInputRef = ref(null);
const isSelectingContact = ref(false);

// dados do contato
const contact = computed(() => task.value?.contact);

const contactDisplayName = computed(
  () => contact.value?.company_name || contact.value?.name || ''
);

function normalizeContact(c) {
  return {
    ...c,
    company_name:
      c.company_name ?? c.additional_attributes?.company_name ?? null,
  };
}

async function searchContacts(query) {
  if (!query || query.length < 1) {
    contactResults.value = [];
    showContactDropdown.value = false;
    return;
  }
  try {
    const { data } = await ContactAPI.search(query, 1, 'name');
    contactResults.value = (data?.payload || []).map(normalizeContact);
    contactHighlightedIndex.value = 0;
    showContactDropdown.value = contactResults.value.length > 0;
  } catch {
    contactResults.value = [];
  }
}

async function selectContact(c) {
  isSelectingContact.value = false;
  showContactDropdown.value = false;
  editingContact.value = false;
  await saveField('contact_id', c.id);
}

function cancelContactEdit() {
  if (isSelectingContact.value) return;
  editingContact.value = false;
  contactSearch.value = '';
  contactResults.value = [];
  showContactDropdown.value = false;
}

function onContactKeydown(e) {
  if (!showContactDropdown.value) return;
  if (e.key === 'Escape') {
    cancelContactEdit();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.min(
      contactHighlightedIndex.value + 1,
      contactResults.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.max(
      contactHighlightedIndex.value - 1,
      0
    );
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = contactResults.value[contactHighlightedIndex.value];
    if (item) selectContact(item);
  }
}

/* eslint-disable no-use-before-define */
function onTypeKeydown(e) {
  if (!showTypeMenu.value) return;
  if (e.key === 'Escape') {
    showTypeMenu.value = false;
    typeSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    typeHighlight.value = Math.min(
      typeHighlight.value + 1,
      filteredTaskTypes.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    typeHighlight.value = Math.max(typeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const tp = filteredTaskTypes.value[typeHighlight.value];
    if (tp) setTaskType(tp.key);
  } else if (e.key === 'Backspace') {
    typeSearch.value = typeSearch.value.slice(0, -1);
    typeHighlight.value = 0;
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    typeSearch.value += e.key;
    typeHighlight.value = 0;
  }
}
/* eslint-enable no-use-before-define */

watch(showTypeMenu, val => {
  if (val) {
    typeHighlight.value = 0;
    window.addEventListener('keydown', onTypeKeydown);
  } else {
    typeSearch.value = '';
    window.removeEventListener('keydown', onTypeKeydown);
  }
});

/* eslint-disable no-use-before-define */
function onPriorityKeydown(e) {
  if (!showPriorityMenu.value) return;
  if (e.key === 'Escape') {
    showPriorityMenu.value = false;
    prioritySearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    priorityHighlight.value = Math.min(
      priorityHighlight.value + 1,
      filteredPriorityOptions.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    priorityHighlight.value = Math.max(priorityHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const opt = filteredPriorityOptions.value[priorityHighlight.value];
    if (opt) setPriority(opt.key);
  } else if (e.key === 'Backspace') {
    prioritySearch.value = prioritySearch.value.slice(0, -1);
    priorityHighlight.value = 0;
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    prioritySearch.value += e.key;
    priorityHighlight.value = 0;
  }
}
/* eslint-enable no-use-before-define */

watch(showPriorityMenu, val => {
  if (val) {
    priorityHighlight.value = 0;
    window.addEventListener('keydown', onPriorityKeydown);
  } else {
    prioritySearch.value = '';
    window.removeEventListener('keydown', onPriorityKeydown);
  }
});

watch(showContactDropdown, val => {
  if (val) window.addEventListener('keydown', onContactKeydown);
  else window.removeEventListener('keydown', onContactKeydown);
});

/* eslint-disable no-use-before-define */
function onAssigneeKeydown(e) {
  if (!showAssigneeMenu.value) return;
  if (e.key === 'Escape') {
    showAssigneeMenu.value = false;
    assigneeSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    assigneeHighlight.value = Math.min(
      assigneeHighlight.value + 1,
      assigneeNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    assigneeHighlight.value = Math.max(assigneeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = assigneeNavigable.value[assigneeHighlight.value];
    if (item) {
      if (item.id === null) setAssignee(null);
      else if (item.id === 'unassigned') setAssignee('unassigned');
      else setAssignee(agents.value?.find(a => a.id === item.id));
    }
  } else if (e.key === 'Backspace') {
    assigneeSearch.value = assigneeSearch.value.slice(0, -1);
    assigneeHighlight.value = 0;
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    assigneeSearch.value += e.key;
    assigneeHighlight.value = 0;
  }
}
/* eslint-enable no-use-before-define */

watch(showAssigneeMenu, val => {
  if (val) {
    assigneeHighlight.value = 0;
    window.addEventListener('keydown', onAssigneeKeydown);
  } else {
    assigneeSearch.value = '';
    window.removeEventListener('keydown', onAssigneeKeydown);
  }
});

function closeDropdowns() {
  showPriorityMenu.value = false;
  showAssigneeMenu.value = false;
  showDatePicker.value = false;
  showTypeMenu.value = false;
  showLabelMenu.value = false;
  showCardMenu.value = false;
  showChecklistAssignee.value = null;
  confirmingId.value = null;
}

function openContactEdit() {
  closeDropdowns();
  contactSearch.value = contactDisplayName.value;
  editingContact.value = true;
  nextTick(() => {
    contactInputRef.value?.focus();
    contactInputRef.value?.select();
  });
}

const datePickerValue = computed({
  get: () => ({
    ...splitDue(task.value?.due_date),
    recurrence_type: task.value?.recurrence_type ?? null,
    recurrence_interval: task.value?.recurrence_interval ?? null,
    recurrence_unit: task.value?.recurrence_unit ?? null,
    recurrence_ends_at: recurrenceEndsToPicker(task.value?.recurrence_ends_at),
    recurrence_count: task.value?.recurrence_count ?? null,
  }),
  set: () => {},
});

async function saveDatePicker(val) {
  // eslint-disable-next-line no-underscore-dangle
  if (val._close) showDatePicker.value = false;
  await updateTask({
    taskId: task.value.id,
    due_date: buildDueIso(val.due_date, val.due_time),
    recurrence_type: val.recurrence_type || null,
    recurrence_interval: val.recurrence_interval || null,
    recurrence_unit: val.recurrence_unit || null,
    recurrence_ends_at: recurrenceEndsToIso(val.recurrence_ends_at),
    recurrence_count: val.recurrence_count || null,
  });
  emit('updated');
}

async function clearModalDueTime() {
  const due_date = task.value.due_date
    ? buildDueIso(dueDateKey(task.value.due_date))
    : null;
  await updateTask({
    taskId: task.value.id,
    due_date,
    recurrence_type: task.value.recurrence_type || null,
    recurrence_interval: task.value.recurrence_interval || null,
    recurrence_unit: task.value.recurrence_unit || null,
    recurrence_ends_at: task.value.recurrence_ends_at
      ? new Date(task.value.recurrence_ends_at * 1000).toISOString()
      : null,
    recurrence_count: task.value.recurrence_count || null,
  });
  emit('updated');
}

const PRIORITY_DOT_OVERRIDE = { no_priority: 'text-n-slate-6' };
const PRIORITY_FLAG_OVERRIDE = { no_priority: 'text-n-slate-8' };

const PRIORITY_OPTIONS = PRIORITY_ORDER.map(key => ({
  key,
  label: t(PRIORITY_META[key].labelKey),
  dot: PRIORITY_DOT_OVERRIDE[key] ?? PRIORITY_META[key].dot,
  flag: PRIORITY_FLAG_OVERRIDE[key] ?? PRIORITY_META[key].dot,
}));

const currentPriority = computed(
  () =>
    PRIORITY_OPTIONS.find(o => o.key === task.value?.priority) ??
    PRIORITY_OPTIONS[3]
);

const filteredAssigneeAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q) return agents.value ?? [];
  return (agents.value ?? [])
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const filteredTaskTypes = computed(() => {
  const q = typeSearch.value.toLowerCase();
  if (!q) return TASK_TYPES;
  return TASK_TYPES.filter(o => o.label.toLowerCase().includes(q));
});

const filteredPriorityOptions = computed(() => {
  const q = prioritySearch.value.toLowerCase();
  if (!q) return PRIORITY_OPTIONS;
  return PRIORITY_OPTIONS.filter(o => o.label.toLowerCase().includes(q));
});

const assigneeNavigable = computed(() => {
  if (!assigneeSearch.value)
    return [
      ...(agents.value ?? []).map(a => ({ id: a.id })),
      { id: 'unassigned' },
    ];
  const items = filteredAssigneeAgents.value.map(a => ({ id: a.id }));
  const showUnassigned = t('TASKS.FILTERS.UNASSIGNED')
    .toLowerCase()
    .includes(assigneeSearch.value.toLowerCase());
  return [...items, ...(showUnassigned ? [{ id: 'unassigned' }] : [])];
});

function isAssigneeHighlighted(id) {
  return (
    assigneeNavigable.value.findIndex(i => i.id === id) ===
    assigneeHighlight.value
  );
}

const { avatarColor, initials: agentInitials } = useAvatarColor();
function initials(name) {
  return agentInitials(name ?? '') || '?';
}

function agentById(id) {
  if (!id) return null;
  return (agents.value ?? []).find(a => a.id === id) ?? null;
}

function checklistItemAvatarStyle(assigneeId) {
  const agent = agentById(assigneeId);
  if (agent?.thumbnail) {
    return {
      backgroundImage: `url(${agent.thumbnail})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  if (assigneeId) {
    return { backgroundColor: avatarColor(agent) };
  }
  return {};
}

function formatDate(ts) {
  if (!ts) return null;
  const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
  const date = d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  const h = d.getHours();
  const m = d.getMinutes();
  const hasTime = h !== 0 || m !== 0;
  if (!hasTime) return date;
  const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return `${date} - ${time}`;
}

const PRIORITY_LABELS = {
  urgent: t('TASKS.PRIORITY.URGENT'),
  high: t('TASKS.PRIORITY.HIGH'),
  normal: t('TASKS.PRIORITY.NORMAL'),
  no_priority: t('TASKS.PRIORITY.NONE'),
};

const TASK_TYPE_LABELS = Object.fromEntries(
  TASK_TYPES.map(tp => [tp.key, tp.label])
);

function parseTags(raw) {
  if (!raw || raw === '[]') return '—';
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length ? arr.join(', ') : '—';
  } catch {
    return raw;
  }
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = String(value);
  return div.innerHTML;
}

function bold(value) {
  return `<strong class="font-semibold text-n-slate-12">${escapeHtml(value)}</strong>`;
}

const ACTIVITY_CONFIG = {
  task_created: {
    icon: 'i-lucide-plus-circle',
    color: 'text-n-teal-11',
    text: () => t('TASKS.MODAL.HISTORY_CREATED'),
  },
  status_changed: {
    icon: 'i-lucide-check-circle',
    color: 'text-n-blue-11',
    text: a =>
      a.new_value === 'completed'
        ? t('TASKS.MODAL.HISTORY_COMPLETED')
        : t('TASKS.MODAL.HISTORY_REOPENED'),
  },
  priority_changed: {
    icon: 'i-lucide-alert-circle',
    color: 'text-n-amber-11',
    text: a =>
      t('TASKS.MODAL.HISTORY_PRIORITY', {
        old: bold(PRIORITY_LABELS[a.old_value] ?? a.old_value ?? '—'),
        new: bold(PRIORITY_LABELS[a.new_value] ?? a.new_value ?? '—'),
      }),
  },
  due_date_changed: {
    icon: 'i-lucide-calendar',
    color: 'text-n-slate-10',
    text: a => {
      const fmt = iso =>
        iso ? formatDate(new Date(iso).getTime() / 1000) : '—';
      return t('TASKS.MODAL.HISTORY_DUE_DATE', {
        old: bold(fmt(a.old_value)),
        new: bold(fmt(a.new_value)),
      });
    },
  },
  assignee_changed: {
    icon: 'i-lucide-user-check',
    color: 'text-n-slate-10',
    text: a =>
      t('TASKS.MODAL.HISTORY_ASSIGNEE', {
        old: bold(a.old_value ?? '—'),
        new: bold(a.new_value ?? '—'),
      }),
  },
  title_changed: {
    icon: 'i-lucide-pencil',
    color: 'text-n-slate-10',
    text: a =>
      a.old_value || a.new_value
        ? t('TASKS.MODAL.HISTORY_TITLE', {
            old: bold(a.old_value ?? '—'),
            new: bold(a.new_value ?? '—'),
          })
        : t('TASKS.MODAL.HISTORY_TITLE_EMPTY'),
  },
  task_type_changed: {
    icon: 'i-lucide-circle-dot',
    color: 'text-n-slate-10',
    text: a =>
      t('TASKS.MODAL.HISTORY_TASK_TYPE', {
        old: bold(TASK_TYPE_LABELS[a.old_value] ?? a.old_value ?? '—'),
        new: bold(TASK_TYPE_LABELS[a.new_value] ?? a.new_value ?? '—'),
      }),
  },
  label_list_changed: {
    icon: 'i-lucide-tag',
    color: 'text-n-amber-11',
    text: a =>
      t('TASKS.MODAL.HISTORY_LABEL_LIST', {
        old: bold(parseTags(a.old_value)),
        new: bold(parseTags(a.new_value)),
      }),
  },
  contact_changed: {
    icon: 'i-lucide-user',
    color: 'text-n-slate-10',
    text: a =>
      t('TASKS.MODAL.HISTORY_CONTACT', {
        old: bold(a.old_value ?? '—'),
        new: bold(a.new_value ?? '—'),
      }),
  },
};

function activityConfig(eventType) {
  return (
    ACTIVITY_CONFIG[eventType] ?? {
      icon: 'i-lucide-activity',
      color: 'text-n-slate-10',
      text: () => eventType,
    }
  );
}

async function loadActivities() {
  try {
    const { data } = await TasksAPI.listActivities(props.taskId);
    activities.value = data.data ?? [];
  } catch {
    activities.value = [];
  }
}

const isLoadingConversation = ref(false);
const contactConversations = ref([]);

async function loadContactConversations(contactId) {
  if (!contactId) return;
  try {
    const { data } = await ContactAPI.getConversations(contactId);
    contactConversations.value = data?.payload ?? [];
  } catch {
    contactConversations.value = [];
  }
}

// ── oportunidade: abre card vinculado ou último do contato ────────
const isLoadingCard = ref(false);
const contactCards = ref([]);

async function loadContactCards(contactId) {
  if (!contactId) return;
  try {
    const { data } = await KanbanAPI.getCardsByContact(contactId);
    contactCards.value = data?.data ?? [];
  } catch {
    contactCards.value = [];
  }
}

async function loadTask() {
  isLoading.value = true;
  contactCards.value = [];
  contactConversations.value = [];
  try {
    await Promise.all([
      store.dispatch('tasks/fetchTask', props.taskId),
      loadActivities(),
    ]);
    titleValue.value = task.value?.title ?? '';
    descriptionValue.value = task.value?.description ?? '';
    const contactId = task.value?.contact?.id;
    if (contactId) {
      if (!task.value?.conversation_id) loadContactConversations(contactId);
      if (!task.value?.kanban_card_id) loadContactCards(contactId);
    }
    if (
      props.openWithChecklistWarning &&
      checklistItems.value.some(i => !i.completed)
    ) {
      showChecklistWarning.value = true;
    }
  } finally {
    isLoading.value = false;
  }
}

async function revertActivity(activityId) {
  revertingId.value = activityId;
  try {
    await TasksAPI.revertActivity(props.taskId, activityId);
    confirmingId.value = null;
    await loadTask();
  } finally {
    revertingId.value = null;
  }
}

async function toggleStatus() {
  if (!task.value) return;
  const newStatus = task.value.status === 'completed' ? 'pending' : 'completed';
  if (
    newStatus === 'completed' &&
    checklistItems.value.some(i => !i.completed)
  ) {
    showChecklistWarning.value = true;
    return;
  }
  showChecklistWarning.value = false;
  const data = { status: newStatus };
  if (newStatus === 'completed') data.completed_at = new Date().toISOString();
  await updateTask({ taskId: task.value.id, ...data });
  loadActivities();
  emit('updated');
}

async function deleteTask() {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TASKS.FORM.DELETE_CONFIRM'))) return;
  if (!(await destroyTask(task.value.id))) return;
  emit('deleted');
  emit('close');
}

async function setPriority(key) {
  showPriorityMenu.value = false;
  await saveField('priority', key);
}

async function setAssignee(agent) {
  showAssigneeMenu.value = false;
  const id =
    agent === null || agent === 'unassigned' ? null : (agent?.id ?? null);
  await saveField('assignee_id', id);
}

function onTitleBlur() {
  // título é obrigatório: apagar tudo restaura o atual em vez de falhar no servidor
  if (!titleValue.value.trim()) {
    titleValue.value = task.value?.title ?? '';
    return;
  }
  if (titleValue.value !== task.value?.title) {
    saveField('title', titleValue.value);
  }
}

async function onSmartPendingFields(fields) {
  if (!task.value) return;
  const saves = [];
  if (fields.description !== null) {
    descriptionValue.value = fields.description;
    saves.push(saveField('description', fields.description));
  }
  if (fields.priority) saves.push(saveField('priority', fields.priority));
  if (fields.assignee_id)
    saves.push(saveField('assignee_id', fields.assignee_id));
  if (fields.task_type) saves.push(saveField('task_type', fields.task_type));
  if (fields.label_list?.length) {
    const existing = task.value.label_list ?? [];
    const merged = [...new Set([...existing, ...fields.label_list])];
    saves.push(saveField('label_list', merged));
  }
  if (fields.due_date) {
    saves.push(
      saveField('due_date', buildDueIso(fields.due_date, fields.due_time))
    );
  }
  if (fields.contact_id) saves.push(saveField('contact_id', fields.contact_id));
  await Promise.all(saves);
}

function onDescriptionBlur() {
  if (descriptionValue.value !== task.value?.description) {
    saveField('description', descriptionValue.value);
  }
}

function toggleCardMenu() {
  if (!showCardMenu.value && cardMenuBtnRef.value) {
    const rect = cardMenuBtnRef.value.getBoundingClientRect();
    cardMenuPos.value = {
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    };
  }
  showCardMenu.value = !showCardMenu.value;
}

function openContactPage() {
  const contactId = task.value?.contact?.id;
  if (!contactId) return;
  window.open(
    `/app/accounts/${accountId.value}/contacts/${contactId}`,
    '_blank'
  );
}

const hasConversation = computed(
  () => !!(task.value?.conversation_id || contactConversations.value.length)
);

async function openLastConversation() {
  if (isLoadingConversation.value) return;

  // Caso 1: conversa explicitamente vinculada à tarefa
  if (task.value?.conversation_display_id) {
    window.open(
      `/app/accounts/${accountId.value}/conversations/${task.value.conversation_display_id}`,
      '_blank'
    );
    return;
  }

  // Caso 2: usa conversas já carregadas do contato
  if (contactConversations.value.length) {
    const last = contactConversations.value.reduce((a, b) =>
      a.id > b.id ? a : b
    );
    window.open(
      `/app/accounts/${accountId.value}/conversations/${last.id}`,
      '_blank'
    );
    return;
  }

  // Caso 3: ainda não carregou — busca agora
  const contactId = task.value?.contact?.id;
  if (!contactId) return;
  isLoadingConversation.value = true;
  try {
    const { data } = await ContactAPI.getConversations(contactId);
    const conversations = data?.payload ?? [];
    contactConversations.value = conversations;
    if (!conversations.length) return;
    const last = conversations.reduce((a, b) => (a.id > b.id ? a : b));
    window.open(
      `/app/accounts/${accountId.value}/conversations/${last.id}`,
      '_blank'
    );
  } finally {
    isLoadingConversation.value = false;
  }
}

const isResolvingLeader = ref(false);

async function resolveLeader() {
  isResolvingLeader.value = true;
  showAssigneeMenu.value = false;
  try {
    const contactId = task.value?.contact?.id;

    const cardId = task.value?.kanban_card_id;
    if (cardId) {
      let card = contactCards.value.find(c => c.id === cardId);
      if (!card) {
        const { data } = await KanbanAPI.getCard(cardId);
        card = data?.data ?? data ?? null;
        if (card) contactCards.value = [...contactCards.value, card];
      }
      const agentId = card?.assignee?.id ?? card?.assignee_id ?? null;
      if (agentId) {
        const agent = (agents.value ?? []).find(a => a.id === agentId);
        if (agent) {
          await setAssignee(agent);
          return;
        }
      }
    }

    if (contactId) {
      let conversations = contactConversations.value;
      if (!conversations.length) {
        const { data } = await ContactAPI.getConversations(contactId);
        conversations = data?.payload ?? [];
        contactConversations.value = conversations;
      }
      if (conversations.length) {
        const last = conversations.reduce((a, b) => (a.id > b.id ? a : b));
        const agentId = last.meta?.assignee?.id ?? null;
        if (agentId) {
          const agent = (agents.value ?? []).find(a => a.id === agentId);
          if (agent) {
            await setAssignee(agent);
          }
        }
      }
    }
  } finally {
    isResolvingLeader.value = false;
  }
}

const hasOpportunity = computed(
  () => !!(task.value?.kanban_card_id || contactCards.value.length)
);

async function openLastOpportunity() {
  if (isLoadingCard.value) return;

  const cardId = task.value?.kanban_card_id;
  const contactId = task.value?.contact?.id;

  // Se já temos os cards carregados, emite direto
  if (contactCards.value.length) {
    const target = cardId
      ? contactCards.value.find(c => c.id === cardId)
      : contactCards.value.reduce((a, b) => (a.id > b.id ? a : b));
    if (target) {
      emit('openCard', target);
      return;
    }
  }

  // Busca os cards do contato (já vêm com pipeline e colunas)
  if (!contactId) return;

  isLoadingCard.value = true;
  try {
    const { data } = await KanbanAPI.getCardsByContact(contactId);
    const cards = data?.data ?? [];
    contactCards.value = cards;
    if (!cards.length) {
      toggleCardMenu();
      return;
    }
    const target = cardId
      ? cards.find(c => c.id === cardId)
      : cards.reduce((a, b) => (a.id > b.id ? a : b));
    if (target) emit('openCard', target);
    else toggleCardMenu();
  } finally {
    isLoadingCard.value = false;
  }
}

// ── vinculação de card kanban ─────────────────────────────────────
const cardSearch = ref('');
const cardResults = ref([]);
const cardHighlight = ref(0);
const isSearchingCards = ref(false);

const pipelines = computed(() => store.getters['kanban/pipelines'] ?? []);

async function searchCards(query) {
  if (!query || query.length < 1) {
    cardResults.value = [];
    return;
  }
  isSearchingCards.value = true;
  try {
    const results = await Promise.all(
      pipelines.value.map(p =>
        KanbanAPI.getCards(p.id, { search: query })
          .then(r =>
            (r.data?.data ?? []).map(c => ({ ...c, pipelineName: p.name }))
          )
          .catch(() => [])
      )
    );
    const flat = results.flat();
    cardResults.value = flat;
    cardHighlight.value = 0;
  } finally {
    isSearchingCards.value = false;
  }
}

async function selectCard(card) {
  showCardMenu.value = false;
  cardSearch.value = '';
  cardResults.value = [];
  await saveField('kanban_card_id', card.id);
}

function onCardMenuKeydown(e) {
  if (!showCardMenu.value) return;
  if (e.key === 'Escape') {
    showCardMenu.value = false;
    cardSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    cardHighlight.value = Math.min(
      cardHighlight.value + 1,
      cardResults.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    cardHighlight.value = Math.max(cardHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const card = cardResults.value[cardHighlight.value];
    if (card) selectCard(card);
  } else if (e.key === 'Backspace') {
    cardSearch.value = cardSearch.value.slice(0, -1);
    searchCards(cardSearch.value);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    cardSearch.value += e.key;
    searchCards(cardSearch.value);
  }
}

function onCardMenuOutsideClick() {
  showCardMenu.value = false;
}

watch(showCardMenu, val => {
  if (val) {
    cardHighlight.value = 0;
    cardSearch.value = '';
    cardResults.value = [];
    window.addEventListener('keydown', onCardMenuKeydown);
    setTimeout(
      () => document.addEventListener('click', onCardMenuOutsideClick),
      0
    );
  } else {
    cardSearch.value = '';
    cardResults.value = [];
    window.removeEventListener('keydown', onCardMenuKeydown);
    document.removeEventListener('click', onCardMenuOutsideClick);
  }
});

function openOnly(key) {
  showPriorityMenu.value = key === 'priority';
  showAssigneeMenu.value = key === 'assignee';
  showDatePicker.value = key === 'date';
  showTypeMenu.value = key === 'type';
  showLabelMenu.value = key === 'label';
  showCardMenu.value = key === 'card';
  showChecklistAssignee.value = null;
  confirmingId.value = null;
}

watch(
  () => props.openWithChecklistWarning,
  val => {
    if (val && checklistItems.value.some(i => !i.completed)) {
      showChecklistWarning.value = true;
    }
  }
);

watch(checklistProgress, progress => {
  if (!progress || progress.done === progress.total) {
    showChecklistWarning.value = false;
  }
});

onMounted(loadTask);
onUnmounted(() => {
  window.removeEventListener('keydown', onContactKeydown);
  window.removeEventListener('keydown', onAssigneeKeydown);
  window.removeEventListener('keydown', onPriorityKeydown);
  window.removeEventListener('keydown', onTypeKeydown);
  window.removeEventListener('keydown', onLabelKeydown);
  window.removeEventListener('keydown', onCardMenuKeydown);
  document.removeEventListener('click', onCardMenuOutsideClick);
});
watch(() => props.taskId, loadTask);
</script>

<template>
  <div class="fixed inset-0 z-50 flex justify-end" @click.self="emit('close')">
    <div
      class="w-full h-full md:w-[540px] bg-n-background shadow-xl flex flex-col md:border-l border-n-weak"
      @click="closeDropdowns"
    >
      <!-- ── Header ── -->
      <div
        class="flex items-center justify-between px-5 py-3 border-b border-n-weak flex-shrink-0"
      >
        <!-- Lado esquerdo: checkbox + datas -->
        <div class="flex items-center gap-2" @click.stop>
          <button
            class="flex-shrink-0 rounded flex items-center justify-center transition-all"
            :class="
              task?.status === 'completed'
                ? 'bg-n-teal-9'
                : 'hover:border-n-teal-6 hover:bg-n-teal-2'
            "
            :style="
              task?.status !== 'completed'
                ? 'width:16px;height:16px;min-width:16px;padding:0;border:1px solid rgb(217,217,224)'
                : 'width:16px;height:16px;min-width:16px;padding:0'
            "
            :title="
              task?.status === 'completed'
                ? t('TASKS.MODAL.MARK_PENDING')
                : t('TASKS.MODAL.MARK_COMPLETE')
            "
            @click="toggleStatus"
          >
            <span
              v-if="task?.status === 'completed'"
              class="i-lucide-check size-2.5 text-white"
            />
          </button>

          <!-- Data de criação + data de conclusão (verde) -->
          <template v-if="task?.status === 'completed'">
            <span class="text-xs text-n-slate-9">
              {{ formatDate(task.created_at) }}
            </span>
            <span class="text-n-slate-9 text-xs">·</span>
            <span class="text-xs text-n-teal-11 font-medium">
              {{ formatDate(task.completed_at ?? task.updated_at) }}
            </span>
          </template>
          <!-- Apenas data de criação quando pendente -->
          <span v-else-if="task" class="text-xs text-n-slate-9">
            {{ formatDate(task.created_at) }}
          </span>
        </div>

        <!-- Lado direito: contato · conversa · oportunidade · lixeira · X -->
        <div class="flex items-center gap-1">
          <!-- Contato: branco=tem contato, cinza inativo=sem contato -->
          <button
            class="p-1.5 transition-colors"
            :class="
              task?.contact?.id
                ? 'text-n-slate-12 hover:text-n-brand'
                : 'text-n-slate-6 cursor-default'
            "
            :title="
              task?.contact?.id ? 'Abrir contato' : 'Sem contato vinculado'
            "
            @click.stop="task?.contact?.id ? openContactPage() : undefined"
          >
            <span class="i-lucide-user size-4" />
          </button>

          <!-- Conversa: branco=tem conversa (vinculada ou do contato), cinza=sem conversa -->
          <button
            class="p-1.5 transition-colors"
            :class="
              hasConversation
                ? 'text-n-slate-12 hover:text-n-brand'
                : 'text-n-slate-6 cursor-default'
            "
            :title="
              task?.conversation_id
                ? 'Abrir conversa vinculada'
                : hasConversation
                  ? 'Abrir última conversa'
                  : 'Sem conversa vinculada'
            "
            @click.stop="hasConversation ? openLastConversation() : undefined"
          >
            <span
              class="size-4"
              :class="
                isLoadingConversation
                  ? 'i-lucide-loader animate-spin'
                  : 'i-lucide-message-square'
              "
            />
          </button>

          <!-- Oportunidade: branco=tem kanban_card_id ou contato tem cards, cinza=sem oportunidade -->
          <button
            ref="cardMenuBtnRef"
            class="p-1.5 transition-colors"
            :class="
              hasOpportunity
                ? 'text-n-slate-12 hover:text-n-brand'
                : 'text-n-slate-6 hover:text-n-brand'
            "
            :title="
              task?.kanban_card_id
                ? t('TASKS.MODAL.OPEN_OPPORTUNITY')
                : hasOpportunity
                  ? 'Abrir última oportunidade'
                  : 'Vincular oportunidade'
            "
            @click.stop="
              hasOpportunity ? openLastOpportunity() : toggleCardMenu()
            "
          >
            <span
              class="size-4"
              :class="
                isLoadingCard
                  ? 'i-lucide-loader animate-spin'
                  : 'i-lucide-filter'
              "
            />
          </button>

          <button
            class="p-1.5 text-n-slate-9 hover:text-n-ruby-9 transition-colors"
            :title="t('TASKS.FORM.DELETE')"
            @click="deleteTask"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>

          <button
            class="p-1.5 text-n-slate-9 hover:text-n-slate-12 transition-colors"
            @click="emit('close')"
          >
            <span
              :class="isMobile ? 'i-lucide-chevron-left' : 'i-lucide-x'"
              class="size-4"
            />
          </button>
        </div>
      </div>

      <!-- ── Loading ── -->
      <div v-if="isLoading" class="flex-1 flex items-center justify-center">
        <span class="i-lucide-loader size-6 text-n-slate-9 animate-spin" />
      </div>

      <!-- ── Body ── -->
      <div v-else-if="task" class="flex-1 overflow-y-auto">
        <div class="px-5 py-3 flex flex-col gap-3">
          <!-- Título -->
          <SmartTitleInput
            v-model="titleValue"
            :placeholder="t('TASKS.FIELDS.TITLE')"
            class="w-full text-lg font-semibold text-n-slate-12 p-0"
            :class="
              task.status === 'completed' ? 'line-through text-n-slate-10' : ''
            "
            @update:pending-fields="onSmartPendingFields"
            @blur="onTitleBlur"
          />

          <!-- Metadados em grid com bordas -->
          <div class="border border-n-weak rounded-lg">
            <!-- Linha 1: Contact | Assignee -->
            <div
              class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-n-weak border-b border-n-weak"
            >
              <!-- Contato (editável) -->
              <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('TASKS.FIELDS.CONTACT') }}
                </p>
                <div class="relative">
                  <div v-if="editingContact" class="flex items-center gap-2">
                    <span
                      class="i-lucide-search size-3.5 text-n-slate-9 flex-shrink-0"
                    />
                    <input
                      ref="contactInputRef"
                      v-model="contactSearch"
                      class="flex-1 text-sm text-n-slate-12 bg-transparent outline-none focus:outline-none border-b border-n-weak min-w-0"
                      :placeholder="t('TASKS.FORM.CONTACT_PLACEHOLDER')"
                      @input="searchContacts($event.target.value)"
                    />
                  </div>
                  <button
                    v-else
                    class="flex items-center gap-2 w-full text-left group/contact"
                    @click="openContactEdit"
                  >
                    <div
                      class="size-6 min-w-6 rounded-full bg-n-surface-2 overflow-hidden flex items-center justify-center flex-shrink-0"
                    >
                      <img
                        v-if="contact?.thumbnail"
                        :src="contact.thumbnail"
                        :alt="contact?.name"
                        class="size-full object-cover"
                      />
                      <span
                        v-else
                        class="text-[10px] font-bold text-n-slate-11 flex items-center justify-center size-full"
                        >{{
                          initials(contact?.company_name || contact?.name)
                        }}</span
                      >
                    </div>
                    <span
                      class="text-sm text-n-slate-12 truncate flex-1 leading-none"
                    >
                      {{ contact?.company_name || contact?.name || '—' }}
                    </span>
                    <span
                      class="i-lucide-pencil size-3 text-n-slate-9 opacity-0 group-hover/contact:opacity-60 transition-opacity flex-shrink-0"
                    />
                  </button>
                  <div
                    v-if="showContactDropdown && contactResults.length"
                    class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-44 overflow-y-auto w-full min-w-[200px]"
                    @mousedown="isSelectingContact = true"
                  >
                    <button
                      v-for="(c, idx) in contactResults"
                      :key="c.id"
                      type="button"
                      class="w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors"
                      :class="
                        idx === contactHighlightedIndex
                          ? 'text-n-brand font-medium bg-n-alpha-1'
                          : 'text-n-slate-12 hover:bg-n-alpha-1'
                      "
                      @click="selectContact(c)"
                    >
                      <div
                        class="size-6 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white bg-n-slate-5"
                        :style="
                          c.thumbnail
                            ? {
                                backgroundImage: `url(${c.thumbnail})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }
                            : {}
                        "
                      >
                        <span v-if="!c.thumbnail">{{
                          initials(c.company_name || c.name)
                        }}</span>
                      </div>
                      <div class="min-w-0">
                        <p class="truncate">{{ c.name }}</p>
                        <p
                          v-if="c.company_name"
                          class="text-n-slate-9 text-xs truncate"
                        >
                          {{ c.company_name }}
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Responsável -->
              <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('TASKS.FIELDS.ASSIGNEE') }}
                </p>
                <button
                  class="flex items-center gap-2 w-full text-left outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                  @click="
                    showAssigneeMenu
                      ? (showAssigneeMenu = false)
                      : openOnly('assignee')
                  "
                >
                  <div
                    class="size-6 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white overflow-hidden"
                    :style="
                      task.assignee
                        ? task.assignee.thumbnail
                          ? {
                              backgroundImage: `url(${task.assignee.thumbnail})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }
                          : { backgroundColor: avatarColor(task.assignee) }
                        : {}
                    "
                    :class="
                      !task.assignee
                        ? 'bg-n-surface-2 border border-n-weak'
                        : ''
                    "
                  >
                    <span v-if="task.assignee && !task.assignee.thumbnail">{{
                      initials(task.assignee.name)
                    }}</span>
                    <span
                      v-else-if="!task.assignee"
                      class="i-lucide-user-plus size-3 text-n-slate-9"
                    />
                  </div>
                  <span
                    v-if="showAssigneeMenu && assigneeSearch"
                    class="text-sm text-n-slate-10 truncate flex-1 leading-none"
                    >{{ assigneeSearch }}</span
                  >
                  <span
                    v-else
                    class="text-sm text-n-slate-12 truncate flex-1 leading-none"
                    >{{ task.assignee?.name || '—' }}</span
                  >
                  <span
                    class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
                  />
                </button>
                <div
                  v-if="showAssigneeMenu"
                  class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-full min-w-[180px] py-1 overflow-hidden"
                  @click.stop
                >
                  <button
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none text-n-slate-12"
                    :disabled="isResolvingLeader"
                    @click="resolveLeader"
                  >
                    <span
                      class="size-3.5 flex-shrink-0"
                      :class="
                        isResolvingLeader
                          ? 'i-lucide-loader animate-spin text-n-slate-9'
                          : 'i-lucide-crown text-n-amber-9'
                      "
                    />
                    <span :class="isResolvingLeader ? 'text-n-slate-9' : ''">
                      {{
                        isResolvingLeader
                          ? $t('KANBAN.TASK_GROUPS.LEADER.RESOLVING')
                          : $t('KANBAN.TASK_GROUPS.LEADER.BUTTON')
                      }}
                    </span>
                  </button>
                  <div class="border-t border-n-weak my-1" />
                  <button
                    v-for="agent in filteredAssigneeAgents"
                    :key="agent.id"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
                    :class="
                      isAssigneeHighlighted(agent.id)
                        ? 'text-n-brand font-medium bg-n-alpha-1'
                        : agent.id === task.assignee?.id
                          ? 'text-n-brand font-medium'
                          : 'text-n-slate-12'
                    "
                    @click="setAssignee(agent)"
                  >
                    <div
                      class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
                      :style="
                        agent.thumbnail
                          ? {
                              backgroundImage: `url(${agent.thumbnail})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }
                          : { backgroundColor: avatarColor(agent) }
                      "
                    >
                      <span v-if="!agent.thumbnail">{{
                        initials(agent.name)
                      }}</span>
                    </div>
                    <span class="truncate">{{ agent.name }}</span>
                    <span
                      v-if="agent.id === task.assignee?.id"
                      class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
                    />
                  </button>
                  <p
                    v-if="assigneeSearch && !filteredAssigneeAgents.length"
                    class="px-3 py-2 text-sm text-n-slate-10 italic"
                  >
                    {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
                  </p>
                  <template
                    v-if="assigneeNavigable.some(i => i.id === 'unassigned')"
                  >
                    <div class="border-t border-n-weak my-1" />
                    <button
                      class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
                      :class="
                        isAssigneeHighlighted('unassigned')
                          ? 'text-n-brand font-medium bg-n-alpha-1'
                          : 'text-n-slate-11'
                      "
                      @click="setAssignee('unassigned')"
                    >
                      <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
                      {{ t('TASKS.FILTERS.UNASSIGNED') }}
                    </button>
                  </template>
                </div>
              </div>
            </div>

            <!-- Linha 2: Due Date | Prioridade -->
            <div
              class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-n-weak border-b border-n-weak"
            >
              <!-- Due Date -->
              <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('TASKS.FIELDS.DUE_DATE') }}
                </p>
                <div class="relative">
                  <button
                    type="button"
                    class="flex items-center gap-1.5 text-sm transition-colors"
                    :class="
                      task.due_date &&
                      new Date(task.due_date * 1000) < new Date()
                        ? 'text-n-ruby-10'
                        : 'text-n-slate-12 hover:text-n-brand'
                    "
                    @click="
                      showDatePicker
                        ? (showDatePicker = false)
                        : openOnly('date')
                    "
                  >
                    <span class="i-lucide-calendar size-3.5 flex-shrink-0" />
                    <span>{{
                      task.due_date
                        ? formatDate(task.due_date)
                        : t('TASKS.DATE_SHORTCUTS.NO_DATE')
                    }}</span>
                    <span
                      v-if="task.recurrence_type"
                      class="i-lucide-repeat size-3 text-n-slate-9"
                    />
                  </button>
                  <div v-if="showDatePicker" class="absolute left-0 top-8 z-50">
                    <TaskDatePicker
                      :model-value="datePickerValue"
                      @update:model-value="saveDatePicker"
                      @clear-time="clearModalDueTime"
                    />
                  </div>
                </div>
              </div>

              <!-- Prioridade -->
              <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('TASKS.FIELDS.PRIORITY') }}
                </p>
                <button
                  class="flex items-center gap-1.5 text-sm w-full text-left outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                  @click="
                    showPriorityMenu
                      ? (showPriorityMenu = false)
                      : openOnly('priority')
                  "
                >
                  <span
                    class="i-lucide-flag size-3.5 flex-shrink-0"
                    :class="currentPriority.flag"
                  />
                  <span
                    v-if="showPriorityMenu && prioritySearch"
                    class="text-n-slate-10 truncate flex-1 leading-none"
                    >{{ prioritySearch }}</span
                  >
                  <span
                    v-else
                    class="text-n-slate-12 truncate flex-1 leading-none"
                    >{{ currentPriority.label }}</span
                  >
                  <span
                    class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
                  />
                </button>
                <div
                  v-if="showPriorityMenu"
                  class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-full min-w-[160px] py-1 overflow-hidden"
                  @click.stop
                >
                  <button
                    v-for="(opt, idx) in filteredPriorityOptions"
                    :key="opt.key"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
                    :class="
                      idx === priorityHighlight
                        ? 'text-n-brand font-medium bg-n-alpha-1'
                        : opt.key === task.value?.priority
                          ? 'text-n-brand font-medium'
                          : 'text-n-slate-12'
                    "
                    @click="setPriority(opt.key)"
                  >
                    <span
                      class="i-lucide-flag size-3.5 flex-shrink-0"
                      :class="opt.flag"
                    />
                    {{ opt.label }}
                    <span
                      v-if="opt.key === task.value?.priority"
                      class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
                    />
                  </button>
                  <p
                    v-if="prioritySearch && !filteredPriorityOptions.length"
                    class="px-3 py-2 text-sm text-n-slate-10 italic"
                  >
                    {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Linha 3: Tipo | Tags -->
            <div
              class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-n-weak"
            >
              <!-- Tipo de tarefa -->
              <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('TASKS.FIELDS.TASK_TYPE') }}
                </p>
                <div class="relative">
                  <button
                    class="flex items-center gap-1.5 text-sm w-full text-left outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                    @click="
                      showTypeMenu ? (showTypeMenu = false) : openOnly('type')
                    "
                  >
                    <span
                      class="size-3.5 flex-shrink-0 text-n-slate-9"
                      :class="[currentType.icon]"
                    />
                    <span
                      v-if="showTypeMenu && typeSearch"
                      class="text-n-slate-10 truncate flex-1 leading-none"
                      >{{ typeSearch }}</span
                    >
                    <span
                      v-else
                      class="text-n-slate-12 truncate flex-1 leading-none"
                      >{{ currentType.label }}</span
                    >
                    <span
                      class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
                    />
                  </button>
                  <div
                    v-if="showTypeMenu"
                    class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-full min-w-[180px] py-1 overflow-hidden"
                    @click.stop
                  >
                    <button
                      v-for="(tp, idx) in filteredTaskTypes"
                      :key="tp.key"
                      class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
                      :class="
                        idx === typeHighlight
                          ? 'text-n-brand font-medium bg-n-alpha-1'
                          : tp.key === task.value?.task_type
                            ? 'text-n-brand font-medium'
                            : 'text-n-slate-12'
                      "
                      @click="setTaskType(tp.key)"
                    >
                      <span class="size-3.5 flex-shrink-0" :class="[tp.icon]" />
                      {{ tp.label }}
                      <span
                        v-if="tp.key === task.value?.task_type"
                        class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
                      />
                    </button>
                    <p
                      v-if="typeSearch && !filteredTaskTypes.length"
                      class="px-3 py-2 text-sm text-n-slate-10 italic"
                    >
                      {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Tags -->
              <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('TASKS.MODAL.TAGS') }}
                </p>
                <!-- trigger / chips + botão abrir -->
                <div class="flex items-center gap-1 flex-wrap">
                  <span
                    v-for="label in task.label_list"
                    :key="label"
                    class="group/lbl inline-flex items-center text-[11px] font-medium px-1.5 py-px rounded-full"
                    :style="{
                      backgroundColor: tagColor(label).bg,
                      color: tagColor(label).text,
                    }"
                  >
                    {{ label }}
                    <span
                      class="overflow-hidden transition-all duration-150 max-w-0 group-hover/lbl:max-w-[12px] flex items-center justify-center"
                    >
                      <button
                        class="flex items-center justify-center hover:bg-black/10 rounded-full ml-[2px]"
                        @click.stop="removeLabel(label)"
                      >
                        <span class="i-lucide-x size-[8px] flex-shrink-0" />
                      </button>
                    </span>
                  </span>
                  <button
                    type="button"
                    class="flex items-center gap-1 text-sm outline-none focus:outline-none"
                    :class="
                      showLabelMenu
                        ? 'text-n-brand'
                        : 'text-n-slate-10 hover:text-n-brand'
                    "
                    @click="
                      showLabelMenu
                        ? (showLabelMenu = false)
                        : openOnly('label')
                    "
                  >
                    <span class="i-lucide-tag size-3.5 flex-shrink-0" />
                    <span
                      v-if="showLabelMenu && labelSearch"
                      class="text-n-slate-10 text-xs"
                      >{{ labelSearch }}</span
                    >
                  </button>
                </div>
                <!-- dropdown -->
                <div
                  v-if="showLabelMenu"
                  class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-52 py-1 overflow-hidden"
                  @click.stop
                >
                  <button
                    v-for="lbl in filteredLabels"
                    :key="lbl.title"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
                    :class="
                      labelNavigable.findIndex(i => i.id === lbl.title) ===
                      labelHighlight
                        ? 'text-n-brand font-medium bg-n-alpha-1'
                        : isLabelActive(lbl.title)
                          ? 'text-n-brand font-medium'
                          : 'text-n-slate-12'
                    "
                    @click="toggleLabel(lbl.title)"
                  >
                    <span
                      class="size-2.5 rounded-full flex-shrink-0"
                      :style="{ backgroundColor: lbl.color }"
                    />
                    <span class="truncate">{{ lbl.title }}</span>
                    <span
                      v-if="isLabelActive(lbl.title)"
                      class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
                    />
                  </button>
                  <p
                    v-if="labelSearch && !filteredLabels.length"
                    class="px-3 py-2 text-sm text-n-slate-10 italic"
                  >
                    {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Separador -->
        <div class="border-t border-n-weak" />

        <!-- Descrição -->
        <div class="px-5 py-4">
          <h4
            class="text-xs font-medium text-n-slate-10 mb-2 uppercase tracking-wide"
          >
            {{ t('TASKS.FIELDS.DESCRIPTION') }}
          </h4>
          <textarea
            v-model="descriptionValue"
            :placeholder="t('TASKS.FORM.DESCRIPTION_PLACEHOLDER')"
            class="w-full text-sm text-n-slate-12 bg-n-surface-2 rounded-lg px-3 py-2 resize-none border border-transparent focus:border-n-brand focus:outline-none placeholder:text-n-slate-10"
            rows="4"
            @blur="onDescriptionBlur"
          />
        </div>

        <!-- Checklist -->
        <div class="px-5 py-4 border-t border-n-weak">
          <div class="flex items-center justify-between mb-3">
            <h4
              class="text-xs font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ t('TASKS.CHECKLIST.TITLE') }}
              <span
                v-if="checklistProgress"
                class="ml-1 text-n-slate-9 normal-case tracking-normal font-normal"
              >
                {{ checklistProgress.done }}/{{ checklistProgress.total }}
              </span>
            </h4>
          </div>

          <!-- Banner checklist incompleto -->
          <div
            v-if="showChecklistWarning"
            class="mb-3 bg-n-amber-2 border border-n-amber-6 text-n-amber-11 rounded-lg px-3 py-2 text-sm flex items-center gap-2"
          >
            <span class="i-lucide-triangle-alert size-4 flex-shrink-0" />
            {{ t('TASKS.CHECKLIST.INCOMPLETE_WARNING') }}
          </div>

          <!-- Itens existentes -->
          <div class="flex flex-col gap-1 mb-3">
            <div
              v-for="(item, idx) in checklistItems"
              :key="item.id"
              draggable="true"
              class="group/ci flex items-center gap-2 py-1 rounded transition-colors"
              :class="dragOverIndex === idx ? 'bg-n-alpha-1' : ''"
              @dragstart="onDragStart(idx)"
              @dragover="onDragOver($event, idx)"
              @dragleave="onDragLeave"
              @drop="onDrop(idx)"
              @dragend="onDragEnd"
            >
              <!-- Grip handle -->
              <span
                class="i-lucide-grip-vertical size-3.5 text-n-slate-7 flex-shrink-0 cursor-grab opacity-0 group-hover/ci:opacity-100 transition-opacity"
              />

              <!-- Checkbox -->
              <button
                class="flex-shrink-0 flex items-center justify-center transition-colors w-5 h-5 min-w-[20px]"
                @click="toggleChecklistItem(item.id)"
              >
                <!-- concluído: square com check -->
                <svg
                  v-if="item.completed"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgb(var(--teal-9))"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="inline-block flex-shrink-0"
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <!-- pendente: square vazio -->
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgb(var(--slate-7))"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="inline-block flex-shrink-0"
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                </svg>
              </button>

              <!-- Texto -->
              <span
                class="flex-1 text-sm min-w-0 truncate"
                :class="
                  item.completed
                    ? 'line-through text-n-slate-10'
                    : 'text-n-slate-12'
                "
              >
                {{ item.text }}
              </span>

              <!-- Avatar responsável do item -->
              <div class="relative flex-shrink-0" @click.stop>
                <button
                  class="size-5 rounded-full flex items-center justify-center hover:opacity-80 transition-all"
                  :style="checklistItemAvatarStyle(item.assignee_id)"
                  :class="
                    !item.assignee_id
                      ? 'bg-n-surface-2 border border-n-weak opacity-0 group-hover/ci:opacity-60'
                      : ''
                  "
                  :title="
                    agentById(item.assignee_id)?.name ??
                    t('TASKS.CHECKLIST.NO_ASSIGNEE')
                  "
                  @click="
                    showChecklistAssignee === item.id
                      ? (showChecklistAssignee = null)
                      : (closeDropdowns(), (showChecklistAssignee = item.id))
                  "
                >
                  <span
                    v-if="
                      item.assignee_id &&
                      !agentById(item.assignee_id)?.thumbnail
                    "
                    class="text-[8px] font-bold text-white"
                    >{{ initials(agentById(item.assignee_id)?.name) }}</span
                  >
                  <span
                    v-else-if="!item.assignee_id"
                    class="i-lucide-user-plus size-2.5 text-n-slate-9"
                  />
                </button>

                <!-- Dropdown agentes do item -->
                <div
                  v-if="showChecklistAssignee === item.id"
                  class="absolute right-0 top-6 z-30 bg-n-background border border-n-weak rounded-lg shadow-xl w-48 overflow-hidden"
                  @click.stop
                >
                  <button
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1"
                    @click="setChecklistItemAssignee(item.id, null)"
                  >
                    <span
                      class="i-lucide-user-x size-3.5 text-n-slate-9 flex-shrink-0"
                    />
                    {{ t('TASKS.CHECKLIST.NO_ASSIGNEE') }}
                  </button>
                  <div class="border-t border-n-weak" />
                  <button
                    v-for="agent in agents"
                    :key="agent.id"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1"
                    :class="
                      agent.id === item.assignee_id
                        ? 'text-n-brand font-medium'
                        : 'text-n-slate-12'
                    "
                    @click="setChecklistItemAssignee(item.id, agent.id)"
                  >
                    <div
                      class="size-4 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                      :style="
                        !agent.thumbnail
                          ? { backgroundColor: avatarColor(agent) }
                          : {}
                      "
                    >
                      <img
                        v-if="agent.thumbnail"
                        :src="agent.thumbnail"
                        class="size-full object-cover"
                      />
                      <span v-else class="text-[8px] font-bold text-white">{{
                        initials(agent.name)
                      }}</span>
                    </div>
                    <span class="truncate">{{ agent.name }}</span>
                    <span
                      v-if="agent.id === item.assignee_id"
                      class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
                    />
                  </button>
                </div>
              </div>

              <!-- Remover item -->
              <button
                class="flex-shrink-0 opacity-0 group-hover/ci:opacity-60 hover:opacity-100 transition-opacity"
                @click="removeChecklistItem(item.id)"
              >
                <span class="i-lucide-x size-3.5 text-n-slate-9" />
              </button>
            </div>
          </div>

          <!-- Input novo item -->
          <div class="flex items-center gap-2">
            <span class="i-lucide-plus size-3.5 text-n-slate-9 flex-shrink-0" />
            <input
              ref="newItemInputRef"
              v-model="newItemText"
              class="flex-1 text-sm text-n-slate-12 bg-transparent outline-none border-none placeholder:text-n-slate-9"
              :placeholder="t('TASKS.CHECKLIST.ADD_PLACEHOLDER')"
              @keydown.enter.prevent="addChecklistItem"
              @keydown.esc="newItemText = ''"
              @click.stop
            />
            <button
              v-if="newItemText.trim()"
              class="text-xs text-n-brand font-medium hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
              @click="addChecklistItem"
            >
              {{ t('TASKS.CHECKLIST.ADD_BUTTON') }}
            </button>
          </div>
        </div>

        <!-- Comentários -->
        <div class="px-5 py-4">
          <TaskComments :task-id="task.id" />
        </div>

        <!-- Separador -->
        <div class="border-t border-n-weak" />

        <!-- Histórico (ordem reversa: mais recente primeiro) -->
        <div class="px-5 py-4">
          <h4
            class="text-xs font-medium text-n-slate-10 mb-3 uppercase tracking-wide"
          >
            {{ t('TASKS.MODAL.HISTORY') }}
          </h4>
          <div v-if="activities.length" class="flex flex-col gap-3">
            <div
              v-for="activity in [...activities].reverse()"
              :key="activity.id"
              class="group/act flex items-start gap-2.5 relative"
            >
              <span
                class="size-4 mt-0.5 flex-shrink-0"
                :class="[
                  activityConfig(activity.event_type).icon,
                  activityConfig(activity.event_type).color,
                ]"
              />
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm text-n-slate-12"
                  v-html="activityConfig(activity.event_type).text(activity)"
                />
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ formatDate(activity.created_at) }}
                  <span v-if="activity.user" class="text-n-slate-9">
                    · {{ activity.user.name }}
                  </span>
                </p>
              </div>

              <!-- Botão desfazer — não aparece para task_created -->
              <div
                v-if="activity.event_type !== 'task_created'"
                class="flex-shrink-0 relative"
              >
                <!-- Popover de confirmação -->
                <div
                  v-if="confirmingId === activity.id"
                  class="absolute right-0 top-6 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl px-3 py-2 flex items-center gap-2 whitespace-nowrap"
                  @click.stop
                >
                  <span class="text-xs text-n-slate-11">{{
                    t('TASKS.MODAL.HISTORY_UNDO_CONFIRM')
                  }}</span>
                  <button
                    class="text-xs text-n-slate-10 hover:opacity-80 transition-opacity"
                    :disabled="revertingId === activity.id"
                    @click.stop="confirmingId = null"
                  >
                    {{ t('TASKS.BULK.CANCEL') }}
                  </button>
                  <button
                    class="text-xs text-n-blue-11 font-medium hover:opacity-80 transition-opacity disabled:opacity-40"
                    :disabled="revertingId === activity.id"
                    @click.stop="revertActivity(activity.id)"
                  >
                    <span
                      v-if="revertingId === activity.id"
                      class="i-lucide-loader size-3 animate-spin inline-block"
                    />
                    <span v-else>{{ t('TASKS.BULK.CONFIRM') }}</span>
                  </button>
                </div>

                <!-- Ícone de desfazer -->
                <button
                  class="p-1 opacity-0 group-hover/act:opacity-60 hover:!opacity-100 transition-opacity"
                  :title="t('TASKS.MODAL.HISTORY_UNDO')"
                  @click.stop="
                    confirmingId =
                      confirmingId === activity.id ? null : activity.id
                  "
                >
                  <span class="i-lucide-rotate-ccw size-3.5 text-n-slate-10" />
                </button>
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10 py-2">
            {{ t('TASKS.MODAL.HISTORY_EMPTY') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Dropdown vincular oportunidade — fora do scroll, dentro do fixed modal -->
    <div
      v-if="showCardMenu && !task?.kanban_card_id"
      class="fixed z-[10100] bg-n-background border border-n-weak rounded-xl shadow-xl w-64 py-1"
      :style="{ top: cardMenuPos.top + 'px', right: cardMenuPos.right + 'px' }"
      @click.stop
    >
      <div class="px-3 py-2 border-b border-n-weak">
        <div class="flex items-center gap-2">
          <span class="i-lucide-search size-3.5 text-n-slate-9 flex-shrink-0" />
          <input
            class="flex-1 text-sm text-n-slate-12 bg-transparent outline-none placeholder:text-n-slate-9"
            :placeholder="t('TASKS.MODAL.OPPORTUNITY_SEARCH_PLACEHOLDER')"
            :value="cardSearch"
            @input="
              e => {
                cardSearch = e.target.value;
                searchCards(e.target.value);
              }
            "
            @click.stop
          />
        </div>
      </div>
      <div
        v-if="isSearchingCards"
        class="px-3 py-2 flex items-center gap-2 text-sm text-n-slate-9"
      >
        <span class="i-lucide-loader size-3.5 animate-spin" />
        {{ t('TASKS.SMART_INPUT.SEARCHING') }}
      </div>
      <template v-else-if="cardSearch && cardResults.length">
        <button
          v-for="(card, idx) in cardResults"
          :key="card.id"
          class="w-full text-left flex flex-col px-3 py-2 text-sm transition-colors"
          :class="
            idx === cardHighlight
              ? 'text-n-brand font-medium bg-n-alpha-1'
              : 'text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="selectCard(card)"
        >
          <span class="truncate">{{
            card.contact?.name || card.title || `Card #${card.id}`
          }}</span>
          <span class="text-xs text-n-slate-9 truncate">{{
            card.pipelineName
          }}</span>
        </button>
      </template>
      <p
        v-else-if="cardSearch && !cardResults.length && !isSearchingCards"
        class="px-3 py-2 text-sm text-n-slate-10 italic"
      >
        {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
      </p>
      <p v-else-if="!cardSearch" class="px-3 py-2 text-sm text-n-slate-10">
        {{ t('TASKS.SMART_INPUT.TYPE_TO_SEARCH') }}
      </p>
    </div>
  </div>
</template>
